import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu as MenuIcon, X, Phone, MessageCircle } from "lucide-react";
import { CONTACT } from "@/data/menu";

const navLinkStyle = (isActive) => ({
  background: isActive ? "var(--goat-orange)" : "transparent",
  color: isActive ? "var(--goat-white)" : "var(--goat-green)",
  border: "2.5px solid var(--goat-green)",
});

export const FloatingActions = () => (
  <div className="fixed left-3 bottom-6 z-50 flex flex-col gap-3" data-testid="floating-actions">
    <a
      href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GOAT Sliders! I'd like to order.")}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-12 h-12 rounded-full flex items-center justify-center"
      style={{ background: "#25D366", border: "2.5px solid var(--goat-green)", boxShadow: "3px 3px 0px var(--goat-green)" }}
      data-testid="floating-whatsapp-btn"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle size={22} color="#FFFFFF" />
    </a>
    <a
      href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
      className="w-12 h-12 rounded-full flex items-center justify-center"
      style={{ background: "var(--goat-white)", border: "2.5px solid var(--goat-green)", boxShadow: "3px 3px 0px var(--goat-green)" }}
      data-testid="floating-call-btn"
      aria-label="Call us"
    >
      <Phone size={22} color="#024423" />
    </a>
  </div>
);

const NAV = [
  { to: "/story", label: "Our Story" },
  { to: "/menu", label: "Menu" },
  { to: "/contact", label: "Contact Us" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: "var(--goat-cream)" }}>
      <header className="sticky top-0 z-50" style={{ background: "var(--goat-cream)", borderBottom: "3px solid var(--goat-green)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between py-3">
          <div className="flex items-center gap-5">
            <Link to="/" data-testid="header-logo-link" aria-label="GOAT Sliders home">
              <img src="/assets/logo.png" alt="GOAT Sliders" className="h-16 md:h-20 w-auto" style={{ mixBlendMode: "multiply" }} data-testid="header-logo" />
            </Link>
            <nav className="hidden lg:flex items-center gap-2" data-testid="header-nav-left">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-left-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-full font-medium uppercase tracking-wide text-sm transition-colors ${
                      isActive ? "text-white" : "hover:text-white"
                    }`
                  }
                  style={({ isActive }) => navLinkStyle(isActive)}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <nav className="hidden md:flex items-center gap-2" data-testid="header-nav">
            <Link
              to="/order"
              data-testid="nav-order-now"
              className="sticker-btn px-5 py-2 font-display uppercase text-sm"
              style={{ background: "var(--goat-orange)", color: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-green)" }}
            >
              Order Now
            </Link>
          </nav>
          <button
            className="md:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={28} color="#024423" /> : <MenuIcon size={28} color="#024423" />}
          </button>
        </div>
        {open && (
          <nav className="md:hidden px-4 pb-4 flex flex-col gap-2" data-testid="mobile-nav" style={{ background: "var(--goat-cream)" }}>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-3 rounded-full font-medium uppercase tracking-wide text-sm"
                style={({ isActive }) => ({
                  background: isActive ? "var(--goat-orange)" : "var(--goat-white)",
                  color: isActive ? "var(--goat-white)" : "var(--goat-green)",
                  border: "2.5px solid var(--goat-green)",
                })}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/order"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-order-now"
              className="sticker-btn px-4 py-3 font-display uppercase text-sm text-center"
              style={{ background: "var(--goat-orange)", color: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-green)" }}
            >
              Order Now
            </Link>
          </nav>
        )}
      </header>

      <main>{children}</main>
      <FloatingActions />

      <footer className="mt-20" style={{ background: "var(--goat-green)" }} data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <img src="/assets/logo.png" alt="GOAT Sliders" className="h-28 w-auto bg-white rounded-2xl p-2" data-testid="footer-logo" />
          <div className="text-center md:text-left">
            <p className="text-lg" style={{ color: "var(--goat-cream)" }}>
              Slidin' Deliciously — fresh, made-to-order sliders.
            </p>
            <p className="mt-2 text-sm" style={{ color: "var(--goat-orange)" }}>
              goatsliders.com
            </p>
            <p className="mt-1 text-sm" style={{ color: "var(--goat-cream)" }} data-testid="footer-fssai">
              FSSAI Lic. No.: 22726692000991
            </p>
          </div>
          <div className="flex gap-3">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sticker-btn px-5 py-3 flex items-center gap-2 font-semibold uppercase text-sm"
              style={{ background: "var(--goat-orange)", color: "var(--goat-white)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
              data-testid="footer-whatsapp-btn"
              aria-label="Chat on WhatsApp"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              className="sticker-btn px-5 py-3 flex items-center gap-2 font-semibold uppercase text-sm"
              style={{ background: "var(--goat-white)", color: "var(--goat-green)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
              data-testid="footer-call-btn"
              aria-label="Call us"
            >
              <Phone size={18} /> Call
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
